import polygonClipping from "polygon-clipping";
import { area, bbox } from "./geometry";

// Flat Glass: window outlines typed in by the tinter (inches). Points are y-down with the top-left of the window at (0,0).
export const SHAPES = [
  { id: "rect", label: "Rectangle", dims: [["w", "Width"], ["h", "Height"]] },
  { id: "arch", label: "Arch (half-round)", dims: [["w", "Width"], ["h", "Height"]], note: "Round top: height includes the half-circle" },
  { id: "eyebrow", label: "Eyebrow arch", dims: [["w", "Width"], ["h", "Height"], ["r", "Rise"]], note: "Rise = how far the curve lifts above the shoulders" },
  { id: "trapezoid", label: "Trapezoid", dims: [["wt", "Top width"], ["wb", "Bottom width"], ["h", "Height"]] },
  { id: "triangle", label: "Triangle", dims: [["w", "Base"], ["h", "Height"]], pick: { key: "peak", label: "Peak", options: [[0, "Centre"], [-1, "Left"], [1, "Right"]] } },
  { id: "oval", label: "Circle / oval", dims: [["w", "Width"], ["h", "Height"]] },
  { id: "quarter", label: "Quarter round", dims: [["w", "Width"], ["h", "Height"]], pick: { key: "corner", label: "Curve", options: [[1, "Top right"], [-1, "Top left"]] } },
  { id: "octagon", label: "Octagon", dims: [["w", "Width"], ["h", "Height"], ["c", "Corner cut"]] },
  { id: "quad", label: "Out of square", dims: [["wt", "Top"], ["wb", "Bottom"], ["hl", "Left"], ["hr", "Right"]], note: "Four edge lengths — bottom-left corner is taken square" },
];
export const shapeOf = (id) => SHAPES.find((s) => s.id === id) || SHAPES[0];
export const MARGIN_OPTIONS = [[0, "exact"], [1 / 16, "+1/16″"], [1 / 8, "+1/8″"], [1 / 4, "+1/4″"]];
export const FLAT_FILM_WIDTHS = [36, 40, 48, 60, 72];
export const FLAT_TYPE = "flat_glass";

const TOL = 0.004;
// points along an arc (centre c, radii rx/ry, from angle a0 to a1) fine enough that no chord sags more than TOL
const arc = (c, rx, ry, a0, a1) => {
  const r = Math.max(rx, ry), n = Math.max(8, Math.ceil(Math.abs(a1 - a0) / (2 * Math.acos(Math.max(-1, 1 - TOL / Math.max(r, TOL * 2))))));
  return Array.from({ length: n + 1 }, (_, i) => { const t = a0 + ((a1 - a0) * i) / n; return [c[0] + rx * Math.cos(t), c[1] + ry * Math.sin(t)]; });
};
const dedupe = (pts) => pts.filter((p, i) => i === 0 || Math.hypot(p[0] - pts[i - 1][0], p[1] - pts[i - 1][1]) > 1e-6).filter((p, i, a) => i < a.length - 1 || Math.hypot(p[0] - a[0][0], p[1] - a[0][1]) > 1e-6);

// what is wrong with the typed sizes (null = fine)
export const dimsError = (shape, d) => {
  const s = shapeOf(shape);
  for (const [k, label] of s.dims) if (!(d[k] > 0)) return `${label} must be more than 0`;
  if (shape === "arch" && d.h < d.w / 2 - 1e-6) return "Height must be at least half the width for a half-round top";
  if (shape === "eyebrow" && (d.r >= d.h || d.r > d.w / 2 + 1e-6)) return "Rise must be smaller than the height and no more than half the width";
  if (shape === "octagon" && d.c * 2 >= Math.min(d.w, d.h)) return "Corner cut must be less than half the shortest side";
  if (shape === "quad") {
    const ac = Math.hypot(d.wb, d.hl);
    if (d.wt + d.hr < ac - 1e-6 || Math.abs(d.wt - d.hr) > ac + 1e-6) return "These four lengths cannot close — re-check the measurements";
  }
  return null;
};

// outline for a shape; `m` = cut margin added on every side
export function shapePoints(shape, d, m = 0) {
  const W = (d.w || 0) + 2 * m, H = (d.h || 0) + 2 * m;
  switch (shape) {
    case "arch": {
      const r = W / 2, top = r;
      return dedupe([[0, H], [0, top], ...arc([r, top], r, r, Math.PI, 2 * Math.PI), [W, H]]);
    }
    case "eyebrow": {
      const r = d.r, half = W / 2, R = (half * half + r * r) / (2 * r), c = [half, r + (R - r)], a = Math.asin(half / R);
      return dedupe([[0, H], [0, r], ...arc(c, R, R, -Math.PI / 2 - a, -Math.PI / 2 + a), [W, H]]);
    }
    case "trapezoid": {
      const wt = d.wt + 2 * m, wb = d.wb + 2 * m, wide = Math.max(wt, wb);
      return [[(wide - wt) / 2, 0], [(wide + wt) / 2, 0], [(wide + wb) / 2, H], [(wide - wb) / 2, H]];
    }
    case "triangle": {
      const px = d.peak === -1 ? 0 : d.peak === 1 ? W : W / 2;
      return [[px, 0], [W, H], [0, H]];
    }
    case "oval":
      return dedupe(arc([W / 2, H / 2], W / 2, H / 2, 0, 2 * Math.PI));
    case "quarter":
      return d.corner === -1
        ? dedupe([[W, H], [W, 0], ...arc([W, H], W, H, -Math.PI / 2, -Math.PI)])
        : dedupe([[0, H], [0, 0], ...arc([0, H], W, H, -Math.PI / 2, 0)]);
    case "octagon": {
      const c = d.c;
      return [[c, 0], [W - c, 0], [W, c], [W, H - c], [W - c, H], [c, H], [0, H - c], [0, c]];
    }
    case "quad": {
      // bottom-left corner square: A = top-left, C = bottom-right, D = top-right where the top and right edges meet
      const wt = d.wt + 2 * m, wb = d.wb + 2 * m, hl = d.hl + 2 * m, hr = d.hr + 2 * m;
      const A = [0, hl], C = [wb, 0], dx = C[0] - A[0], dy = C[1] - A[1], L = Math.hypot(dx, dy);
      const a = (wt * wt - hr * hr + L * L) / (2 * L), h = Math.sqrt(Math.max(0, wt * wt - a * a));
      const mx = A[0] + (a * dx) / L, my = A[1] + (a * dy) / L;
      const cands = [[mx + (h * -dy) / L, my + (h * dx) / L], [mx - (h * -dy) / L, my - (h * dx) / L]];
      const D = cands.sort((p, q) => q[1] - p[1])[0]; // the upper solution
      const top = Math.max(hl, D[1]);
      return [[0, top - hl], [D[0], top - D[1]], [wb, top], [0, top]];
    }
    default:
      return [[0, 0], [W, 0], [W, H], [0, H]];
  }
}

// split an outline into left / right parts at x (butt seam, no overlap)
export function splitAt(points, x) {
  const bb = bbox(points), big = 1e4;
  const part = (x0, x1) => {
    const r = polygonClipping.intersection([points.map(([a, b]) => [a, b])], [[[x0, -big], [x1, -big], [x1, big], [x0, big], [x0, -big]]]);
    const rings = r.flatMap((poly) => poly.slice(0, 1)).map((ring) => dedupe(ring));
    return rings.sort((a, b) => area(b) - area(a))[0] || null;
  };
  if (!(x > bb.minX + 0.5) || !(x < bb.maxX - 0.5)) return null;
  const L = part(bb.minX - 1, x), R = part(x, bb.maxX + 1);
  return L && R ? [L, R] : null;
}

const fmt = (n) => (Math.abs(n - Math.round(n)) < 1e-6 ? String(Math.round(n)) : (+n.toFixed(2)).toString());
export const sizeLabel = (shape, d) => {
  if (shape === "quad") return `${fmt(d.wt)}/${fmt(d.wb)} × ${fmt(d.hl)}/${fmt(d.hr)}″`;
  if (shape === "trapezoid") return `${fmt(d.wt)}/${fmt(d.wb)} × ${fmt(d.h)}″`;
  return `${fmt(d.w)} × ${fmt(d.h)}″`;
};
export const windowName = (w) => w.label?.trim() || `${shapeOf(w.shape).label} ${sizeLabel(w.shape, w.dims)}`;
export const windowArea = (w) => area(shapePoints(w.shape, w.dims, w.margin || 0)) * (w.qty || 1); // sq in, cut size
export const sqft = (sqin) => sqin / 144;

// every roll piece a window produces (qty copies × seam halves)
export function windowPieces(w, ownerId) {
  const pts = shapePoints(w.shape, w.dims, w.margin || 0);
  const parts = w.seam != null ? splitAt(pts, w.seam) : null;
  const outlines = parts ? [["L", parts[0]], ["R", parts[1]]] : [[null, pts]];
  const name = windowName(w), out = [];
  for (let k = 0; k < (w.qty || 1); k++) {
    for (const [part, p] of outlines) {
      const bb = bbox(p), shifted = p.map(([x, y]) => [x - bb.minX, y - bb.minY]);
      out.push({
        id: `flat-${w.id}-${k + 1}${part ? `-${part}` : ""}`, patternId: `flat:${w.id}`, flatId: w.id, flatCopy: k + 1, flatPart: part,
        name: `${name}${w.qty > 1 ? ` ${k + 1}/${w.qty}` : ""}${part ? ` · ${part === "L" ? "left" : "right"} of seam` : ""}`,
        pieceType: FLAT_TYPE, side: "center", version: 1, ownerId, setKey: "", setName: "", setOffset: null,
        measure: { w: bb.w, h: bb.h }, rawPoints: shifted, points: shifted, basePoints: shifted, holes: [],
        x: 0, y: 0, rotation: 0, included: false, appliedOffset: 0, dirty: false, cleaned: true, autoFixed: true,
      });
    }
  }
  return out;
}

// true when the window (cut size) cannot lie on the film either way round
export const tooWideFor = (w, rollWidth, edge = 0) => { const bb = bbox(shapePoints(w.shape, w.dims, w.margin || 0)); return Math.min(bb.w, bb.h) + 2 * edge > rollWidth + 1e-6; };
export const needsRoll = (w, edge = 0) => { const bb = bbox(shapePoints(w.shape, w.dims, w.margin || 0)); return Math.ceil(Math.min(bb.w, bb.h) + 2 * edge); };
export const newWindowId = () => `w${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
